import React, { useState } from "react";
import { useNavigate } from "react-router";
import { formatCurrency } from "../../lib/formatCurrency";
import { IncidentModel } from "../reduxToolkit/incidentsSlice";
import { useAppSelector } from "../hooks/reduxToolkit";
import { selectTodos } from "../reduxToolkit/selectors/todos";
import { ToDoList, ToDoListItem } from "./todoList";
import { Tooltip } from "./tooltip";
import { useSubscriptionPrompt } from "../hooks/useSubscriptionPrompt";
import { useTerminology } from "../hooks/useTerminology";
import { useMinistryQualification, useSharingMinistry } from "../hooks/useSharingMinistry";
import { useIncidentSubmittabilityFor } from "../hooks/useIncidentSubmittability";
import { NotSubmittableReason } from "../../lib/rules/incidentSubmittability";
import { selectIncidentRequiresFamilyPlan } from "../reduxToolkit/selectors/requiredPlan";

import './incidentSubmitButton.scss'

interface IncidentSubmitButtonProps {
  buttonType?: string

  incident: IncidentModel
}

export function IncidentSubmitButton({buttonType, incident}: IncidentSubmitButtonProps) {
  const navigate = useNavigate()
  const terms = useTerminology()

  const sharingMinistry = useSharingMinistry()
  const { ministryName, qualifyingAmount } = useMinistryQualification()

  // Whether this incident can be submitted is a business rule evaluated from
  // current state, NOT inferred from todo state. Todos are generated on the
  // backend and take a round-trip to arrive; an interactive control can't wait
  // on that. See lib/rules/incidentSubmittability.ts.
  const submittability = useIncidentSubmittabilityFor(incident)

  // "Reimbursement" applies to the HRA flow; CHM/Sedera submissions are for "sharing".
  // When no pending submission exists yet, fall back to the member's customization
  // (sharing-ministry members default to sharing; HRA-only members to reimbursement).
  const isHraSubmission =
    submittability.pendingSubmission?.submission_type === 'HRA' ||
    (!submittability.pendingSubmission && !sharingMinistry)
  const SubmissionAction = isHraSubmission ? 'Reimbursement' : 'Sharing'
  const submissionAction = isHraSubmission ? 'reimbursement' : 'sharing'

  // The Solo plan only covers submissions for the membership creator — an
  // incident for anyone else requires the Family plan (upgrade prompt).
  const requiresFamily = useAppSelector(selectIncidentRequiresFamilyPlan(incident))

  const { checkSubscription, SubscriptionPromptModal } = useSubscriptionPrompt({
    trigger: 'always',
    title: `Subscribe to Submit for ${SubmissionAction}`,
    message: `Subscribe to submit your ${terms.incidents} for ${submissionAction} and access all submission features.`,
    requiresFamilyPlan: () => requiresFamily,
    upgradeTitle: 'Family Plan Required',
    upgradeMessage: `Your Solo plan covers ${submissionAction} submissions for yourself. Upgrade to the Family plan to submit for other members of your household.`,
  })

  // Each blocker names the todo the backend generates for it. Resolve those to
  // real todos where they exist, so the list keeps the todo's own title, action,
  // and explainer route; blockers whose todo hasn't synced down yet fall back to
  // the blocker's own title and render as informational.
  const openTodos = useAppSelector(selectTodos())
  const blockerTodos = submittability.blockers
    .flatMap((blocker) => openTodos.filter((todo) => todo.key === blocker.todoKey))
  const unrealizedBlockers = submittability.blockers
    .filter((blocker) => !openTodos.some((todo) => todo.key === blocker.todoKey))

  const [show, setShow] = useState(false)

  if (submittability.reason) {
    const { wording, why } = explainNotSubmittable(submittability.reason, {
      incidentTerm: terms.incident,
      submissionAction,
      ministryName,
      qualifyingAmount: formatCurrency(qualifyingAmount),
    })

    return <div className="incident-submit-button">
      <Tooltip tooltip={why}>
        <button className={`btn btn-outline-secondary disabled incident-submit-button__toggle`} type="button">
          {wording}
        </button>
      </Tooltip>
    </div>
  }

  const { canSubmit, canSkip, resumeSubmissionId } = submittability

  const _onClick = () => {
    if (resumeSubmissionId) {
      if (!checkSubscription()) { return }
      navigate(`/incidents/${incident.id}/submit/${resumeSubmissionId}`)
    } else if (canSubmit) {
      if (!checkSubscription()) { return }
      navigate(`/incidents/${incident.id}/submit`)
    } else {
      setShow(!show)
    }
  }

  const handleSkipClick = (e: React.MouseEvent) => {
    e.preventDefault()

    // Check subscription before allowing skip
    if (!checkSubscription()) {
      return
    }

    navigate(`/incidents/${incident.id}/submit`)
  }

  let submitText = `Submit for ${SubmissionAction}`
  if (resumeSubmissionId) {
    submitText = 'Continue Submission in Progress'
  } else {
    if (show) {
      submitText = 'Complete These Actions First'
    }
    if (show && canSubmit) {
      submitText = 'Ready to Submit'
    }
  }

  return <>
    <div className="incident-submit-button">
      <button className={`btn btn-${buttonType || 'primary'} incident-submit-button__toggle`} type="button"
          data-can-submit={canSubmit}
          aria-expanded={show}
          onClick={_onClick}>
        {submitText}
      </button>
      <div className={`collapse ${show && 'show'}`}>
        <ToDoList key={`incident/${incident.id}/todos`} todos={blockerTodos} />
        {unrealizedBlockers.map((blocker) =>
          <ToDoListItem key={blocker.todoKey} item={{ title: blocker.title }} />)}
        {canSkip &&
          <button onClick={handleSkipClick} className="btn btn-sm btn-link incident-submit-button__skip">
            <small>skip this and just give me my documents</small>
          </button>}
      </div>
    </div>
    <SubscriptionPromptModal />
  </>

}

/**
 * The user-facing explanation for each reason there is nothing to submit.
 *
 * `NotSubmittableReason` is exhaustive, so every case is covered here and the
 * component no longer needs the "this should not happen" Sentry report that the
 * old todo-absence heuristic required.
 */
function explainNotSubmittable(
  reason: NotSubmittableReason,
  {
    incidentTerm,
    submissionAction,
    ministryName,
    qualifyingAmount,
  }: {
    incidentTerm: string,
    submissionAction: string,
    ministryName: string,
    qualifyingAmount: string,
  },
): { wording: string, why: string } {
  switch (reason) {
    case 'no-expenses':
      return {
        wording: 'Nothing to Submit',
        why: `There are no expenses on this ${incidentTerm} yet.`,
      }
    case 'all-expenses-submitted':
      return {
        wording: 'Nothing to Submit',
        why: `All your expenses for this ${incidentTerm} have already been submitted.`,
      }
    case 'expenses-incomplete':
      return {
        wording: 'Cannot Submit',
        why: 'Some of your expenses are missing data.  Please complete them before submitting.',
      }
    case 'zero-paid-amount':
      return {
        wording: 'Cannot Submit',
        why: 'You cannot submit expenses with $0 paid amount. Please update the paid amount for your expenses.',
      }
    case 'all-paid-with-hra':
      return {
        wording: 'Nothing to Submit',
        why: 'All of your expenses have been automatically submitted to the HRA because you used your HRA card.  ' +
          `Once your expense total crosses over ${qualifyingAmount}, you can submit to ${ministryName} for sharing.`,
      }
    case 'no-submission-destination':
      return {
        wording: 'Nothing to Submit',
        why: `Your membership isn't set up for ${submissionAction}, so there's nothing to submit for this ${incidentTerm}.`,
      }
    case 'below-qualifying-amount':
      return {
        wording: 'Nothing to Submit',
        why: `The total value of your expenses is less than ${qualifyingAmount}, so you can't submit them for sharing yet.`,
      }
    case 'incident-closed':
      return {
        wording: 'Nothing to Submit',
        why: `This ${incidentTerm} is closed. Reopen it to submit its expenses.`,
      }
  }
}
